import { useUserStore } from '@/stores/userStore'
import type {
  SSEMessageCallback,
  SSEErrorCallback,
  SSEDoneCallback,
} from '@/api/types/chat.type'

// Python服务的基础URL（开发环境指向 localhost:7001，生产环境由 .env.production 注入）
const PYTHON_BASE_URL = import.meta.env.VITE_PYTHON_BASE_URL ?? 'https://ai-llm.yichenglink.com'

/**
 * 获取请求头
 */
const getHeaders = () => {
  const headers: Record<string, string> = {}
  const { currentUser } = useUserStore.getState()

  if (currentUser?.memberToken) {
    headers['memberToken'] = currentUser.memberToken
  }

  const deviceToken = import.meta.env.VITE_DEVICE_TOKEN
  if (deviceToken) {
    headers['deviceToken'] = deviceToken
  }

  return headers
}

/**
 * SSE连接配置接口
 */
export interface SSEConfig {
  /** 消息回调 */
  onMessage?: SSEMessageCallback
  /** 错误回调 */
  onError?: SSEErrorCallback
  /** 完成回调 */
  onDone?: SSEDoneCallback
  /** 超时时间（毫秒） */
  timeout?: number
}

/**
 * SSE连接类
 */
export class SSEConnection {
  private eventSource: EventSource | null = null
  private config: SSEConfig
  private timeoutId: NodeJS.Timeout | null = null

  constructor(config: SSEConfig = {}) {
    this.config = config
  }

  /**
   * 连接到SSE端点
   * @param url 端点URL
   * @param data POST数据
   */
  connect(url: string, data: any): void {
    try {
      console.log('SSE连接开始，URL:', url)
      console.log('SSE连接数据:', data)

      // 创建URLSearchParams用于POST数据
      const params = new URLSearchParams()
      Object.entries(data).forEach(([key, value]) => {
        if (typeof value === 'object') {
          params.append(key, JSON.stringify(value))
        } else {
          params.append(key, String(value))
        }
      })

      // 创建EventSource
      const fullUrl = `${PYTHON_BASE_URL}${url}?${params.toString()}`
      console.log('SSE完整URL:', fullUrl)

      this.eventSource = new EventSource(fullUrl)
      console.log('EventSource已创建')

      // 设置超时
      if (this.config.timeout) {
        this.timeoutId = setTimeout(() => {
          console.log('SSE连接超时')
          this.close()
          this.config.onError?.(new Event('timeout'))
        }, this.config.timeout)
      }

      // 监听消息
      this.eventSource.onmessage = (event) => {
        console.log('SSE收到消息:', event.data)
        try {
          // const data = JSON.parse(event.data)
          this.config.onMessage?.({
            type: 'message',
            data: event.data,
            id: event.lastEventId,
          })
        } catch (error) {
          console.error('SSE消息解析错误:', error)
        }
      }

      // 监听错误
      this.eventSource.onerror = (error) => {
        console.error('SSE连接错误:', error)
        console.error('SSE连接状态:', this.eventSource?.readyState)
        this.config.onError?.(error)
        this.close()
      }

      // 监听连接打开
      this.eventSource.onopen = () => {
        console.log('SSE连接已建立，状态:', this.eventSource?.readyState)
      }
    } catch (error) {
      console.error('SSE连接创建失败:', error)
      this.config.onError?.(error as Event)
    }
  }

  /**
   * 关闭SSE连接
   */
  close(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }

    if (this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
      this.config.onDone?.()
    }
  }

  /**
   * 检查连接状态
   */
  isConnected(): boolean {
    return this.eventSource?.readyState === EventSource.OPEN
  }
}

/**
 * 使用fetch发送POST请求并处理SSE响应
 * @param url 端点URL
 * @param data POST数据
 * @param config SSE配置
 * @returns Promise<void>
 */
export async function fetchWithSSE(
  url: string,
  data: any,
  config: SSEConfig = {}
): Promise<void> {
  try {
    console.log('fetchWithSSE开始，URL:', url)
    console.log('fetchWithSSE数据:', data)

    const headers = getHeaders()
    headers['Content-Type'] = 'application/json'

    const fullUrl = `${PYTHON_BASE_URL}${url}`
    console.log('fetchWithSSE完整URL:', fullUrl)
    console.log('fetchWithSSE请求体:', JSON.stringify(data))

    // 添加更多调试信息
    console.log('fetchWithSSE请求配置:', {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    })

    const response = await fetch(fullUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
      mode: 'cors',
      credentials: 'omit',
    })

    console.log('fetchWithSSE响应状态:', response.status)
    console.log(
      'fetchWithSSE响应头:',
      Object.fromEntries(response.headers.entries())
    )

    if (!response.ok) {
      const errorText = await response.text()
      console.error('fetchWithSSE响应错误:', errorText)
      throw new Error(
        `HTTP error! status: ${response.status}, body: ${errorText}`
      )
    }

    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('Response body is null')
    }

    console.log('fetchWithSSE开始读取响应流...')

    const decoder = new TextDecoder('utf-8')

    while (true) {
      const { value, done } = await reader.read()
      if (done) {
        console.log('fetchWithSSE响应流读取完成')
        config.onDone?.()
        break
      }

      const chunk = decoder.decode(value, { stream: true })
      console.log('fetchWithSSE收到原始数据块:', chunk)

      // SSE 规范的数据格式通常是以 "data:" 开头
      chunk.split('\n').forEach((line) => {
        if (line.startsWith('data:')) {
          try {
            const data = line.replace(/^data:\s*/, '')
            console.log('fetchWithSSE收到SSE数据:', data)

            if (data === '[DONE]') {
              console.log('fetchWithSSE收到结束信号')
              config.onDone?.()
              return
            }

            const parsedData = JSON.parse(data)
            config.onMessage?.({
              type: 'message',
              data: JSON.stringify(parsedData), // 传递 JSON 字符串，保持与 EventSource 格式一致
              id: undefined,
            })
          } catch (e) {
            console.log('fetchWithSSE非JSON数据:', line)
          }
        }
      })
    }
  } catch (error) {
    console.error('fetchWithSSE请求失败:', error)
    console.error('fetchWithSSE错误详情:', {
      name: (error as Error).name,
      message: (error as Error).message,
      stack: (error as Error).stack,
    })
    config.onError?.(error as Event)
  }
}
